import React, { useState, useRef } from 'react';
import emailjs from '@emailjs/browser';
import ReCAPTCHA from 'react-google-recaptcha';
import { emailjsConfig } from '../config/emailjs';
import { uploadToCloudinary } from '../config/cloudinary';

const SubmitClip: React.FC = () => {
  const [formData, setFormData] = useState({
    userName: '',
    userEmail: '',
    gamerTag: '',
    socialLinks: {
      youtube: '',
      twitch: '',
      x: '',
      instagram: '',
      steam: ''
    },
    mapName: '',
    clipType: '',
    description: '',
    clipUrl: '',
    additionalNotes: ''
  });
  const [attachments, setAttachments] = useState<File[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const recaptchaRef = useRef<ReCAPTCHA>(null);

  const maps = [
    'Ancient', 'Dust2', 'Inferno', 'Mirage', 'Nuke', 'Overpass', 'Train', 'Vertigo', 'Anubis'
  ];

  const clipTypes = [
    'Smoke Lineup', 'Flash Lineup', 'Molotov/Incendiary', 'HE Grenade', 'One-way Smoke', 'Other'
  ];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    
    // Handle social links separately
    if (name.startsWith('social_')) {
      const socialPlatform = name.replace('social_', '');
      setFormData(prev => ({
        ...prev,
        socialLinks: {
          ...prev.socialLinks,
          [socialPlatform]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    
    // Validate file types and sizes
    const validFiles = files.filter(file => {
      const isValidType = file.type.startsWith('image/') || 
                         ['.png', '.jpg', '.jpeg', '.gif', '.webp'].some(ext => 
                           file.name.toLowerCase().endsWith(ext));
      const isValidSize = file.size <= 10 * 1024 * 1024; // 10MB limit
      
      if (!isValidType) {
        alert(`File ${file.name} is not a valid image format.`);
        return false;
      }
      if (!isValidSize) {
        alert(`File ${file.name} is too large. Maximum size is 10MB.`);
        return false;
      }
      return true;
    });
    
    // Limit to 5 files total
    if (attachments.length + validFiles.length > 5) {
      alert('Maximum 5 files allowed.');
      return;
    }
    
    setAttachments(prev => [...prev, ...validFiles]);
  };

  const removeAttachment = (index: number) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const handleCaptchaChange = (token: string | null) => {
    setCaptchaToken(token);
  };

  const handleCaptchaExpired = () => {
    setCaptchaToken(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Check if reCAPTCHA is completed
    if (!captchaToken) {
      alert('Please complete the reCAPTCHA verification.');
      return;
    }
    
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      // Upload files to Cloudinary if attachments exist
      let uploadedFileUrls: string[] = [];
      if (attachments.length > 0) {
        try {
          const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
          const submitterName = formData.userName.replace(/[^a-zA-Z0-9]/g, '_');
          
          const uploadPromises = attachments.map((file, index) => {
            // Create custom file name with submitter info and timestamp
            // Check if filename already has an extension
            const fileNameParts = file.name.split('.');
            const fileExtension = fileNameParts.length > 1 ? fileNameParts.pop() : '';
            const baseFileName = fileNameParts.join('.');
            const customFileName = fileExtension 
              ? `cs2_lineups/${submitterName}_${timestamp}_${index + 1}.${fileExtension}`
              : `cs2_lineups/${submitterName}_${timestamp}_${index + 1}`;
            
            return uploadToCloudinary(file, customFileName);
          });
          
          uploadedFileUrls = await Promise.all(uploadPromises);
          console.log('Files uploaded to Cloudinary:', uploadedFileUrls);
        } catch (error) {
          console.error('Error uploading files:', error);
          // Continue with email even if file upload fails
        }
      }

              // Prepare template parameters
        const socialLinksText = Object.entries(formData.socialLinks)
          .filter(([_, url]) => url.trim() !== '')
          .map(([platform, url]) => `${platform}: ${url}`)
          .join('\n');
          
        // Use full template parameters since we know the issue is with Handlebars syntax
        const templateParams = {
          from_name: formData.userName || 'Anonymous',
          from_email: formData.userEmail || 'No email provided',
          gamer_tag: formData.gamerTag || 'No gamer tag provided',
          social_links: socialLinksText || 'No social links provided',
          map_name: formData.mapName || 'No map selected',
          clip_type: formData.clipType || 'No type selected',
          description: formData.description || 'No description provided',
          clip_url: formData.clipUrl || 'No URL provided',
          additional_notes: formData.additionalNotes || 'No additional notes',
          attachment_count: attachments.length > 0 ? `${attachments.length} file(s) uploaded` : 'No attachments',
          file_urls: uploadedFileUrls.length > 0 ? uploadedFileUrls.join('\n') : 'No files uploaded',
          submission_id: `${formData.userName.replace(/[^a-zA-Z0-9]/g, '_')}_${new Date().toISOString().replace(/[:.]/g, '-')}`
        };

        console.log('Template parameters being sent:', templateParams);

      // Send email using EmailJS configuration
      await emailjs.send(
        emailjsConfig.serviceId,
        emailjsConfig.templateId,
        templateParams,
        emailjsConfig.publicKey
      );

      setSubmitStatus('success');
      setFormData({
        userName: '',
        userEmail: '',
        gamerTag: '',
        socialLinks: {
          youtube: '',
          twitch: '',
          x: '',
          instagram: '',
          steam: ''
        },
        mapName: '',
        clipType: '',
        description: '',
        clipUrl: '',
        additionalNotes: ''
      });
      setAttachments([]);
      setCaptchaToken(null);
      recaptchaRef.current?.reset();
    } catch (error) {
      console.error('Error sending email:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 py-12">
      <div className="max-w-4xl mx-auto px-6">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-yellow-400 mb-4">Submit Your Clip</h1>
          <p className="text-gray-300 text-lg max-w-2xl mx-auto">
            Share your best CS2 lineups, utility throws, or tactical plays with the community. 
            Help other players improve their game!
          </p>
        </div>

        <div className="bg-gray-800 rounded-xl shadow-2xl border border-gray-700 p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* User Information */}
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="userName" className="block text-sm font-medium text-gray-300 mb-2">
                  Your Name *
                </label>
                <input
                  type="text"
                  id="userName"
                  name="userName"
                  value={formData.userName}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-yellow-400 focus:border-transparent transition-all duration-200"
                  placeholder="Enter your name"
                />
              </div>
              <div>
                <label htmlFor="userEmail" className="block text-sm font-medium text-gray-300 mb-2">
                  Your Email *
                </label>
                <input
                  type="email"
                  id="userEmail"
                  name="userEmail"
                  value={formData.userEmail}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-yellow-400 focus:border-transparent transition-all duration-200"
                  placeholder="your.email@example.com"
                />
              </div>
            </div>

            {/* Gaming Credentials */}
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="gamerTag" className="block text-sm font-medium text-gray-300 mb-2">
                  Gamer Tag / Steam Name
                </label>
                <input
                  type="text"
                  id="gamerTag"
                  name="gamerTag"
                  value={formData.gamerTag}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-yellow-400 focus:border-transparent transition-all duration-200"
                  placeholder="Your CS2 gamer tag or Steam name"
                />
                <p className="text-gray-400 text-sm mt-1">
                  How you want to be credited for your clip
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Social Media Links (Optional)
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label htmlFor="social_youtube" className="block text-xs text-gray-400 mb-1">
                      YouTube
                    </label>
                    <input
                      type="url"
                      id="social_youtube"
                      name="social_youtube"
                      value={formData.socialLinks.youtube}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-yellow-400 focus:border-transparent transition-all duration-200 text-sm"
                      placeholder="https://youtube.com/@username"
                    />
                  </div>
                  <div>
                    <label htmlFor="social_twitch" className="block text-xs text-gray-400 mb-1">
                      Twitch
                    </label>
                    <input
                      type="url"
                      id="social_twitch"
                      name="social_twitch"
                      value={formData.socialLinks.twitch}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-yellow-400 focus:border-transparent transition-all duration-200 text-sm"
                      placeholder="https://twitch.tv/username"
                    />
                  </div>
                  <div>
                    <label htmlFor="social_x" className="block text-xs text-gray-400 mb-1">
                      X
                    </label>
                    <input
                      type="url"
                      id="social_x"
                      name="social_x"
                      value={formData.socialLinks.x}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-yellow-400 focus:border-transparent transition-all duration-200 text-sm"
                      placeholder="https://x.com/username"
                    />
                  </div>
                  <div>
                    <label htmlFor="social_instagram" className="block text-xs text-gray-400 mb-1">
                      Instagram
                    </label>
                    <input
                      type="url"
                      id="social_instagram"
                      name="social_instagram"
                      value={formData.socialLinks.instagram}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-yellow-400 focus:border-transparent transition-all duration-200 text-sm"
                      placeholder="https://instagram.com/username"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label htmlFor="social_steam" className="block text-xs text-gray-400 mb-1">
                      Steam Profile
                    </label>
                    <input
                      type="url"
                      id="social_steam"
                      name="social_steam"
                      value={formData.socialLinks.steam}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-yellow-400 focus:border-transparent transition-all duration-200 text-sm"
                      placeholder="https://steamcommunity.com/id/username"
                    />
                  </div>
                </div>
                <p className="text-gray-400 text-sm mt-2">
                  Add your social media profiles to be credited properly
                </p>
              </div>
            </div>

            {/* Clip Information */}
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="mapName" className="block text-sm font-medium text-gray-300 mb-2">
                  Map *
                </label>
                <select
                  id="mapName"
                  name="mapName"
                  value={formData.mapName}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-yellow-400 focus:border-transparent transition-all duration-200"
                >
                  <option value="">Select a map</option>
                  {maps.map(map => (
                    <option key={map} value={map}>{map}</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="clipType" className="block text-sm font-medium text-gray-300 mb-2">
                  Clip Type *
                </label>
                <select
                  id="clipType"
                  name="clipType"
                  value={formData.clipType}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-yellow-400 focus:border-transparent transition-all duration-200"
                >
                  <option value="">Select clip type</option>
                  {clipTypes.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Clip URL */}
            <div>
              <label htmlFor="clipUrl" className="block text-sm font-medium text-gray-300 mb-2">
                Clip URL *
              </label>
              <input
                type="url"
                id="clipUrl"
                name="clipUrl"
                value={formData.clipUrl}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-yellow-400 focus:border-transparent transition-all duration-200"
                placeholder="https://youtube.com/watch?v=... or https://clips.twitch.tv/..."
              />
              <p className="text-gray-400 text-sm mt-2">
                Please provide a link to your clip (YouTube, Twitch, Streamable, etc.)
              </p>
            </div>

            {/* Description */}
            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-300 mb-2">
                Description *
              </label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                required
                rows={4}
                className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-yellow-400 focus:border-transparent transition-all duration-200 resize-vertical"
                placeholder="Describe your lineup or play. Include details like positioning, timing, and what makes it effective..."
              />
            </div>

            {/* Additional Notes */}
            <div>
              <label htmlFor="additionalNotes" className="block text-sm font-medium text-gray-300 mb-2">
                Additional Notes
              </label>
              <textarea
                id="additionalNotes"
                name="additionalNotes"
                value={formData.additionalNotes}
                onChange={handleInputChange}
                rows={3}
                className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-yellow-400 focus:border-transparent transition-all duration-200 resize-vertical"
                placeholder="Any additional information, tips, or context you'd like to share..."
              />
            </div>

            {/* File Attachments */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Attachments (Screenshots, Photos)
              </label>
              <div className="space-y-4">
                {/* File Upload Input */}
                <div className="relative">
                  <input
                    type="file"
                    id="attachments"
                    multiple
                    accept="image/*,.png,.jpg,.jpeg,.gif,.webp"
                    onChange={handleFileChange}
                    className="hidden"
                  />
                  <label
                    htmlFor="attachments"
                    className="flex items-center justify-center gap-2 w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white hover:bg-gray-600 cursor-pointer transition-all duration-200 border-dashed border-2 hover:border-yellow-400"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                    <span>Choose files or drag and drop</span>
                  </label>
                </div>

                {/* File List */}
                {attachments.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-sm text-gray-400">Selected files:</p>
                    {attachments.map((file, index) => (
                      <div key={index} className="flex items-center justify-between bg-gray-700 rounded-lg p-3">
                        <div className="flex items-center gap-3">
                          <svg className="w-5 h-5 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          <div>
                            <p className="text-white text-sm font-medium">{file.name}</p>
                            <p className="text-gray-400 text-xs">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeAttachment(index)}
                          className="text-red-400 hover:text-red-300 transition-colors duration-200"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <p className="text-gray-400 text-sm mt-2">
                Accepted formats: PNG, JPG, JPEG, GIF, WebP (Max 5 files, 10MB each)
              </p>
            </div>

            {/* reCAPTCHA */}
            <div className="flex justify-center pt-6">
              <div className="bg-gray-700 rounded-lg p-4">
                <ReCAPTCHA
                  ref={recaptchaRef}
                  sitekey="6LdGAI0rAAAAAFK-5_dQbiGKnL773QD-HQ0rXWnv"
                  onChange={handleCaptchaChange}
                  onExpired={handleCaptchaExpired}
                  theme="dark"
                  size="normal"
                />
              </div>
            </div>

            {/* Submit Button */}
            <div className="flex justify-center pt-6">
              <button
                type="submit"
                disabled={isSubmitting || !captchaToken}
                className={`px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-200 ${
                  isSubmitting || !captchaToken
                    ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                    : 'bg-yellow-400 text-gray-900 hover:bg-yellow-300 hover:scale-105 shadow-lg hover:shadow-yellow-400/25'
                }`}
              >
                {isSubmitting ? (
                  <span className="flex items-center gap-2">
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Submitting...
                  </span>
                ) : !captchaToken ? (
                  'Complete reCAPTCHA to Submit'
                ) : (
                  'Submit Clip'
                )}
              </button>
            </div>

            {/* Status Messages */}
            {submitStatus === 'success' && (
              <div className="bg-green-900/50 border border-green-700 rounded-lg p-4 text-center">
                <div className="flex items-center justify-center gap-2 text-green-400">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="font-semibold">Clip submitted successfully!</span>
                </div>
                <p className="text-green-300 text-sm mt-2">
                  Thank you for your submission. We'll review your clip and get back to you soon.
                </p>
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="bg-red-900/50 border border-red-700 rounded-lg p-4 text-center">
                <div className="flex items-center justify-center gap-2 text-red-400">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  <span className="font-semibold">Error submitting clip</span>
                </div>
                <p className="text-red-300 text-sm mt-2">
                  There was an error sending your submission. Please try again or contact us directly.
                </p>
              </div>
            )}
          </form>
        </div>

        {/* Information Section */}
        <div className="mt-12 bg-gray-800/50 rounded-lg border border-gray-700 p-6">
          <h2 className="text-xl font-semibold text-yellow-400 mb-4">Submission Guidelines</h2>
          <div className="grid md:grid-cols-2 gap-6 text-gray-300">
            <div>
              <h3 className="font-semibold text-white mb-2">What we're looking for:</h3>
              <ul className="space-y-1 text-sm">
                <li>• Clear, high-quality footage</li>
                <li>• Unique or effective lineups</li>
                <li>• Step-by-step instructions</li>
                <li>• Creative utility usage</li>
                <li>• Team coordination plays</li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold text-white mb-2">Requirements:</h3>
              <ul className="space-y-1 text-sm">
                <li>• Video must be accessible via public URL</li>
                <li>• Include detailed description</li>
                <li>• Original content only</li>
                <li>• No offensive language or behavior</li>
                <li>• CS2 content only</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubmitClip; 